<script>
import Multiselect from 'vue-multiselect'

import { apiSecond } from '@/boot/axios'
import './admin.css'
import {
	categoryBadgeStyle,
	formatUpdateDateTime,
	apiErrorMessage,
} from '@/components/system-updates/utils'

const PER_PAGE_OPTIONS = [10, 20, 50, 100]
const STATUSES = ['draft', 'scheduled', 'published']

function positiveInt(value, fallback) {
	const number = Number(value)
	return Number.isInteger(number) && number > 0 ? number : fallback
}

// Админка: список обновлений системы. Фильтры и страница живут в query,
// чтобы после редактирования «Назад» возвращал туда же.
export default {
	name: 'SystemUpdatesAdmin',
	components: { Multiselect },
	data() {
		const query = this.$route.query
		const perPage = positiveInt(query.per_page, 20)
		return {
			headers: [
				{
					text: this.$t('headers.action'),
					value: 'action',
					align: 'left',
					sortable: false,
					width: '100px',
				},
				{
					text: this.$t('system_updates.admin.col_title'),
					value: 'title_ru',
					cellClass: 'su-admin-col-title',
				},
				{ text: this.$t('system_updates.admin.col_category'), value: 'category', width: '160px' },
				{ text: this.$t('system_updates.admin.col_status'), value: 'status', width: '150px' },
				{
					text: this.$t('system_updates.admin.col_published'),
					value: 'published_at',
					width: '150px',
				},
				{ text: this.$t('system_updates.admin.col_popup'), value: 'show_popup', width: '110px' },
				{ text: this.$t('system_updates.admin.col_views'), value: 'views_count', width: '110px' },
			],
			list: [],
			total: 0,
			loading: false,
			error: null,
			requestSeq: 0,

			categories: [],
			filters: {
				search: typeof query.search === 'string' ? query.search : '',
				category: null,
				status: null,
			},
			options: {
				page: positiveInt(query.page, 1),
				itemsPerPage: PER_PAGE_OPTIONS.includes(perPage) ? perPage : 20,
			},
			perPageOptions: PER_PAGE_OPTIONS,
			deletingId: null,
			// «Сохранено» после возврата из формы (params.saved, в URL не попадает)
			savedToast: this.$route.params.saved === '1',
		}
	},
	computed: {
		canManage() {
			return this.$store.getters.userPermission(['manage_system_updates'])
		},
		canManageCategories() {
			return this.$store.getters.userPermission(['manage_system_update_categories'])
		},
		statusOptions() {
			return [{ value: null, label: this.$t('system_updates.admin.status_all') }].concat(
				STATUSES.map((value) => ({
					value,
					label: this.$t('system_updates.admin.status_' + value),
				})),
			)
		},
		categoryOptions() {
			return [{ id: null, name: this.$t('system_updates.admin.category_all') }].concat(
				this.categories.map((c) => ({ id: c.id, name: c.name_ru || c.name_kz || '#' + c.id })),
			)
		},
	},
	watch: {
		'$route.query'() {
			this.syncFiltersFromQuery()
			this.loadList()
		},
		// Пагинация таблицы → query; сам список грузится уже по смене query
		options: {
			handler(options) {
				const query = this.$route.query
				const perPage = positiveInt(query.per_page, 20)
				if (options.itemsPerPage !== perPage) {
					this.updateQuery({ page: undefined, per_page: String(options.itemsPerPage) })
				} else if (options.page !== positiveInt(query.page, 1)) {
					this.updateQuery({ page: options.page > 1 ? String(options.page) : undefined })
				}
			},
			deep: true,
		},
	},
	mounted() {
		this.syncFiltersFromQuery()
		this.loadList()
		this.loadCategories()
	},
	methods: {
		categoryBadgeStyle,
		formatUpdateDateTime,

		syncFiltersFromQuery() {
			const query = this.$route.query
			const perPage = positiveInt(query.per_page, 20)
			this.options.page = positiveInt(query.page, 1)
			this.options.itemsPerPage = PER_PAGE_OPTIONS.includes(perPage) ? perPage : 20
			this.filters.search = typeof query.search === 'string' ? query.search : ''
			const status = STATUSES.includes(query.status) ? query.status : null
			this.filters.status =
				this.statusOptions.find((o) => o.value === status) || this.statusOptions[0]
			const categoryId = positiveInt(query.category_id, null)
			this.filters.category =
				this.categoryOptions.find((o) => o.id === categoryId) ||
				(categoryId ? { id: categoryId, name: '#' + categoryId } : this.categoryOptions[0])
		},

		async loadCategories() {
			if (!this.canManage) return
			// Список категорий отдаёт только админский роут под своим правом; без него
			// фильтр берём из публичного списка
			try {
				if (this.canManageCategories) {
					const { data } = await apiSecond.get('v1/admin/system-update-categories')
					this.categories = Array.isArray(data && data.data) ? data.data : []
				} else {
					const { data } = await apiSecond.get('v1/public/system-update-categories', {
						headers: { 'Accept-Language': 'ru' },
					})
					this.categories = (Array.isArray(data && data.data) ? data.data : []).map((c) => ({
						id: c.id,
						name_ru: c.name,
					}))
				}
				this.syncFiltersFromQuery()
			} catch (e) {
				this.categories = []
			}
		},

		async loadList() {
			if (!this.canManage) return
			const seq = ++this.requestSeq
			const query = this.$route.query
			const params = {
				page: positiveInt(query.page, 1),
				per_page: this.options.itemsPerPage,
			}
			if (query.search) params.search = String(query.search).slice(0, 255)
			if (positiveInt(query.category_id, null)) params.category_id = positiveInt(query.category_id)
			if (STATUSES.includes(query.status)) params.status = query.status

			this.loading = true
			this.error = null
			try {
				const { data } = await apiSecond.get('v1/admin/system-updates', { params })
				if (seq !== this.requestSeq) return
				this.list = Array.isArray(data && data.data) ? data.data : []
				this.total = Number(data && data.total) || 0

				// Удалили последнюю запись на странице — уходим на предыдущую
				const lastPage = Number(data && data.last_page) || 1
				if (!this.list.length && params.page > 1 && params.page > lastPage) {
					this.updateQuery({ page: String(lastPage) })
				}
			} catch (error) {
				if (seq !== this.requestSeq) return
				this.list = []
				this.total = 0
				this.error = apiErrorMessage(error, this.$t('system_updates.load_error'))
			} finally {
				if (seq === this.requestSeq) this.loading = false
			}
		},

		updateQuery(patch) {
			const query = { ...this.$route.query, ...patch }
			Object.keys(query).forEach((key) => {
				if (query[key] === undefined || query[key] === null || query[key] === '') delete query[key]
			})
			if (JSON.stringify(query) === JSON.stringify(this.$route.query)) {
				this.loadList()
				return
			}
			this.$router.replace({ query }).catch(() => {})
		},

		search() {
			this.updateQuery({
				page: undefined,
				search: (this.filters.search || '').trim().slice(0, 255) || undefined,
				category_id:
					this.filters.category && this.filters.category.id
						? String(this.filters.category.id)
						: undefined,
				status:
					this.filters.status && this.filters.status.value ? this.filters.status.value : undefined,
			})
		},

		resetFilters() {
			this.filters.search = ''
			this.filters.category = this.categoryOptions[0]
			this.filters.status = this.statusOptions[0]
			this.search()
		},

		statusClass(status) {
			return 'su-status su-status--' + (STATUSES.includes(status) ? status : 'draft')
		},

		statusLabel(status) {
			return STATUSES.includes(status)
				? this.$t('system_updates.admin.status_' + status)
				: status || '—'
		},

		editLink(item) {
			return {
				path: '/' + this.$i18n.locale + '/account/system-updates/' + item.id + '/edit',
				query: { back: this.$route.fullPath },
			}
		},

		async remove(item) {
			if (this.deletingId) return
			const confirmed = await this.$dialog.confirm({
				title: this.$t('system_updates.admin.delete_confirm', {
					title: item.title_ru || '#' + item.id,
				}),
				text: this.$t('system_updates.admin.delete_text'),
				confirmText: this.$t('button.delete'),
			})
			if (!confirmed) return

			this.deletingId = item.id
			try {
				await apiSecond.delete('v1/admin/system-updates/' + item.id)
				this.loadList()
			} catch (error) {
				// Уже удалили в соседней вкладке — просто обновляем список
				if (error && error.response && error.response.status === 404) {
					this.loadList()
				} else {
					this.$dialog.alert({
						title: apiErrorMessage(error, this.$t('system_updates.admin.delete_error')),
					})
				}
			} finally {
				this.deletingId = null
			}
		},
	},
}
</script>

<template>
	<div class="wrap__content" :class="{ 'wrap__content--preloader': !$store.state.userObject }">
		<v-preloader v-if="!$store.state.userObject"></v-preloader>

		<div v-else class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="['SystemUpdatesAdmin']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content">
							<div class="card__content-header">
								<div
									class="content__title w-full flex flex-wrap justify-between items-center gap-3"
								>
									<div class="content__title--element">
										<div class="content__title--text">
											{{ $t('sidebar.system_updates') }}
										</div>
									</div>
									<div v-if="canManage" class="ml-auto">
										<router-link
											:to="'/' + $i18n.locale + '/account/system-updates/create'"
											class="btn--link btn--link--plus"
										>
											{{ $t('system_updates.admin.add') }}
											<img src="@/assets/img/icon-plus.svg" />
										</router-link>
									</div>
								</div>
							</div>

							<div class="card__content--body">
								<div v-if="!canManage" class="su-admin-denied">
									{{ $t('system_updates.admin.forbidden') }}
								</div>

								<div v-else class="table__block--content">
									<div class="w-full row gap-y-2 mb-4 su-admin-filters">
										<div class="col-12 col-md-6 col-lg-3">
											<label class="search__label">
												<input
													v-model="filters.search"
													type="text"
													maxlength="255"
													:placeholder="$t('system_updates.admin.search')"
													@keyup.enter="search"
												/>
											</label>
										</div>
										<div class="col-12 col-md-6 col-lg-3">
											<div class="form__line--input">
												<Multiselect
													v-model="filters.category"
													:options="categoryOptions"
													:searchable="false"
													:close-on-select="true"
													:show-labels="false"
													:allow-empty="false"
													label="name"
													track-by="id"
													class="multiselect__check"
												/>
											</div>
										</div>
										<div class="col-12 col-md-6 col-lg-3">
											<div class="form__line--input">
												<Multiselect
													v-model="filters.status"
													:options="statusOptions"
													:searchable="false"
													:close-on-select="true"
													:show-labels="false"
													:allow-empty="false"
													label="label"
													track-by="value"
													class="multiselect__check"
												/>
											</div>
										</div>
										<div class="col-12 col-md-6 col-lg-3 su-filter-actions">
											<button class="input-btn form__line--btn" @click="search">
												{{ $t('button.find') }}
											</button>
											<button class="btn--link btn--link--plus" @click.prevent.stop="resetFilters">
												{{ $t('button.clear') }}
											</button>
										</div>
									</div>

									<div v-if="error" class="su-admin-error">
										<span>{{ error }}</span>
										<button type="button" class="su-admin-link" @click="loadList">
											{{ $t('system_updates.retry') }}
										</button>
									</div>

									<v-data-table
										:headers="headers"
										:items="list"
										:loading="loading"
										:loading-text="$t('button.loading')"
										:no-data-text="$t('system_updates.admin.empty')"
										disable-sort
										:options.sync="options"
										:server-items-length="total"
										:footer-props="{ 'items-per-page-options': perPageOptions }"
									>
										<template slot="item.action" slot-scope="{ item }">
											<div class="flex">
												<router-link
													:to="editLink(item)"
													class="tb__table--btn tb__table--edit"
													v-tooltip.top-center="{
														content: $t('tooltip.edit'),
														class: ['tooltip__btn'],
													}"
												>
													<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M9 15h6.75M12.375 2.625a1.591 1.591 0 112.25 2.25L5.25 14.25l-3 .75.75-3 9.375-9.375z" stroke="#52A5FC" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
												</router-link>
												<a
													class="tb__table--btn tb__table--delete"
													:class="{ 'su-admin-busy': deletingId === item.id }"
													v-tooltip.top-center="{
														content: $t('tooltip.remove'),
														class: ['tooltip__btn'],
													}"
													@click="remove(item)"
												>
													<svg version="1.0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" v-bind:svg-inline="''" v-bind:style="'width: 18px'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><g fill="red"><path d="M213.9 1.8c-7.8 2.1-15.1 6.6-21.2 13.1-9.6 10.3-11.7 17.7-11.7 41.8V75H60l.2 30.2.3 30.3h391l.3-30.3.2-30.2H331.1l-.3-20.3c-.3-18.3-.6-20.7-2.7-26-4.3-10.9-15-21.4-26.1-25.9C296.7.6 295.2.5 258.5.3c-33.5-.2-38.8 0-44.6 1.5zM293 32.2c7.2 3.8 8 6.4 8 26V75h-90V58.2c0-19.5.8-22.1 8-25.9 4.2-2.3 4.9-2.3 36.9-2.3 31.9 0 32.8.1 37.1 2.2zM93 166.3c0 .2 6.1 69.8 13.5 154.7 8.7 100.3 14 156.3 15 159.6 4.3 14.3 16.7 25.9 32.2 29.9 8.6 2.2 196 2.2 204.6 0 15.5-4 27.9-15.6 32.2-29.9 1-3.3 6.3-59.3 15-159.6 7.4-84.9 13.5-154.5 13.5-154.7 0-.2-73.3-.3-163-.3-89.6 0-163 .1-163 .3zm117.8 157.4l-.3 82.8-14.7.3-14.8.3V241h30l-.2 82.7zm60.2.3v83h-30V241h30v83zm60 0v83.1l-14.7-.3-14.8-.3-.3-82.8-.2-82.7h30v83z"/></g></svg>
												</a>
											</div>
										</template>
										<template slot="item.title_ru" slot-scope="{ item }">
											<router-link :to="editLink(item)" class="su-admin-title">
												{{ item.title_ru }}
											</router-link>
										</template>
										<template slot="item.category" slot-scope="{ item }">
											<span
												v-if="item.category"
												class="su-admin-badge"
												:style="categoryBadgeStyle(item.category)"
											>
												{{ item.category.name }}
											</span>
											<span v-else>—</span>
										</template>
										<template slot="item.status" slot-scope="{ item }">
											<span :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span>
										</template>
										<template slot="item.published_at" slot-scope="{ item }">
											{{ item.published_at ? formatUpdateDateTime(item.published_at) : '—' }}
										</template>
										<template slot="item.show_popup" slot-scope="{ item }">
											{{
												item.show_popup
													? $t('system_updates.admin.yes')
													: $t('system_updates.admin.no')
											}}
										</template>
										<template slot="item.views_count" slot-scope="{ item }">
											{{ item.views_count || 0 }}
										</template>
									</v-data-table>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<v-snackbar v-model="savedToast" :timeout="3000" color="#2BB673" top right>
			{{ $t('system_updates.admin.saved') }}
		</v-snackbar>
	</div>
</template>
